<template>
  <div class="account" :class="platform">
    <div class="account-left">
      <StIcon class="account-logo" :name="account.icon" size="24" />
      <div class="account-currency">
        <p class="currency-code">{{ account.code }}</p>
        <p class="currency-name">{{ account.name }}</p>
      </div>
    </div>

    <div class="account-right">
      <div class="account-balance-wrapper">
        <p class="account-balance" data-t="account-balance">
          {{ formatCrypto(account.balance) }}
        </p>
        <p class="account-fiat">
          {{ formatCurrency(account.fiatBalance) }}
        </p>
      </div>
      <StTooltip>
        <template #activator>
          <StToggle v-model="isVisible" :disabled="isDisabled" size="m" />
        </template>
        <div v-if="isDisabled" class="tooltip-content">
          {{
            disabled
              ? t('payments.manageAssets.lastAccount')
              : t('payments.manageAssets.nonZeroBalance')
          }}
        </div>
      </StTooltip>
    </div>
  </div>
</template>
<script setup lang="ts">
import { useCurrenciesStore } from '../../../stores/useCurrenciesStore'

const { account, disabled } = defineProps<{
  account: ExtendedUserAccount
  disabled: boolean
}>()

const emit = defineEmits<{
  updateVisible: [options: { id: number; value: boolean }]
}>()

const { t } = useI18n()
const { platform } = usePlatform()

const currenciesStore = useCurrenciesStore()
const { appCurrency } = storeToRefs(currenciesStore)

const { format: formatCrypto } = useCryptoFormatter()
const { format: formatCurrency } = useCurrencyFormatter({
  currency: appCurrency.value.code,
})

const isVisible = computed<boolean>({
  get: () => account.isVisible,
  set: (value) => emit('updateVisible', { id: account.id, value }),
})

const isDisabled = computed(
  () => (+account.balance !== 0 || disabled) && account.isVisible,
)
</script>

<style scoped>
.account {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-100) 0;
}

.account-left {
  display: flex;
  gap: var(--spacing-100);
  align-items: center;
}

.account-currency {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-025);
}

.currency-code {
  margin: 0;
  font: var(--desktop-text-md-semibold);
}

.currency-name {
  margin: 0;
  font: var(--desktop-text-xs-medium);
  color: var(--text-tertiary);
}

.account-right {
  display: flex;
  gap: var(--spacing-150);
  align-items: center;
}

.account-balance-wrapper {
  display: flex;
  flex-direction: column;
  align-items: end;
}

.account-balance {
  margin: 0;
  font: var(--desktop-text-md-semibold);
}

.account-fiat {
  margin: 0;
  font: var(--desktop-text-xs-medium);
  color: var(--text-tertiary);
}

.tooltip-content {
  max-width: 176px;
  padding: var(--spacing-100) var(--spacing-150);
  font: var(--desktop-text-xs-medium);
  text-align: center;
}

.mobile {
  .currency-code {
    font: var(--mobile-text-medium);
  }

  .currency-name {
    font: var(--mobile-caption-1-regular);
  }

  .account-balance {
    font: var(--mobile-text-medium);
  }

  .account-fiat {
    font: var(--mobile-caption-1-regular);
  }

  .account-right {
    gap: var(--spacing-100);
  }
}
</style>
